import { pollInterval, useAppQuery, useCurrentSubscriptionId } from "@phonero/common-ux"
import { CheckVippsPaymentDocument, OrderDetailFragment } from "./PaymentCallback.generated"
import { OrderStatusId, PaymentOperation } from "@phonero/common-graphql/types"
import { useEffect } from "react"
import { uncompletedPurchases } from "../../../util/reactiveVars"

export const usePaymentDetailPoller = (paymentID?: string) => {
  // NOTE: becuase of a race-condition with the cache, this might return data for the wrong items.
  /// Apollo is being really naughty here...
  const { subscriptionId } = useCurrentSubscriptionId()
  console.log("usePaymentDetailPollerV3", paymentID, subscriptionId)

  // nosemgrep: semgrep.gql-missing-error-check-warning
  const paymentDetail = useAppQuery(CheckVippsPaymentDocument, {
    onCompleted: (d) => {
      if (!!paymentID && !sameId(paymentID, d.checkVippsPayment)) {
        return
      }
      if (d.checkVippsPayment?.order?.status === OrderStatusId.Completed) {
        paymentDetail.stopPolling()
      }
    },
    pollInterval: pollInterval(1000),
    skip:
      !paymentID ||
      !subscriptionId,
    variables: {
      paymentID: paymentID!,
    },
  })

  const vippsPaymentStatus = !!paymentID ? sameId(paymentID, paymentDetail.data?.checkVippsPayment) : null
  const order: OrderDetailFragment | undefined = vippsPaymentStatus?.order ?? undefined

  useEffect(() => {
    if(!paymentID)
      return
    // The purpose of this effect is to automatically mark the stored uncompletedPurchases as completed (which will remove them)
    // for cases where we know the user does not need to perform any action on them.
    switch (order?.status) {
/*      case OrderStatusId.Pendingdelivery:
      case OrderStatusId.Pendingapproval:
      case OrderStatusId.Registered:*/
      case OrderStatusId.Completed:
      case OrderStatusId.Cancelled:
        uncompletedPurchases.remove(paymentID)
        return
    }
    if (!vippsPaymentStatus?.states) {
      return
    }
    const isCompleted = vippsPaymentStatus.states.some(
      p => p?.rawOperation && [
        PaymentOperation.Captured.toString(),
        PaymentOperation.Cancelled.toString(),
        PaymentOperation.Refund.toString()].indexOf(p.rawOperation) > -1
    )
    if(isCompleted) {
      uncompletedPurchases.remove(
        vippsPaymentStatus.id
      )
    }
  }, [
    order?.status,
    paymentID,
    vippsPaymentStatus?.id,
    vippsPaymentStatus?.states,
  ])

  /**
   The last states, ordered by time, latest first.
   */
  const states = uniques(
    [...(vippsPaymentStatus?.states || [])].filter(Boolean),
    (t) => "" + t?.operation + t?.time
  ).sort(sortTime)

  const lastState = states?.[0]
  const productId = vippsPaymentStatus?.productID

  if(!paymentID)
    return undefined

  return {
    lastState,
    states,
    productId,
    order,
    paymentDetail,
    loading: paymentDetail.loading
  }
}

const sortTime = <T extends { time?: Date | string | number } | null>(
  a: T,
  b: T
) => {
  const A = a?.time || ""
  const B = b?.time || ""
  if (A > B) {
    return -1
  }
  if (A < B) {
    return 1
  }
  return 0
}

const sameId = <Identifiable extends { id: string } | undefined | null>(
  id: string,
  item: Identifiable
) => {
  if (!item) {
    return null
  }
  if (item.id === id) {
    return item
  }
  return null
}

/**
 retuns only the chosen uniqe elements, based on any criteria

 if the key-param returns a boolean, the item will be forced included if true, and skipped on false.
 */
const uniques = <T extends any>(
  items: T[],
  key: (item: T) => string | number | boolean
): T[] => {
  const reduced = items.reduce((r, t) => {
    const k = key(t)
    if (typeof k === "boolean") {
      if (k) {
        return { ...r, [Math.random().toString()]: t }
      }
      return r
    }
    return { ...r, [k]: t }
  }, {} as Record<string | number | symbol, T>)
  return Object.values(reduced)
}
